import { Messages } from '~src/codegen/Localize';
import { ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg, StandardActionToastMessages } from '~src/commonInfra/contentActions/ActionHelpers';
import { IErrorDetail } from '~src/commonInfra/contentActions/ActionMessages';
import { IErrorItem } from '~src/commonInfra/contentActions/MultipleItemResult';
import { DataSources } from '~src/content/resources/Datasources';
import { Workbooks } from '~src/content/resources/Workbooks';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ISchedule } from '~src/schedule/types/ISchedule';
import { IActionContext } from '~src/ts/helpers/ActionContext';
import { L10n } from '~src/ts/L10n/L10n';
import { IContentInfo } from '~src/ts/models/IContentInfo';
import { IDataSourceBase, IDataSourceInfo } from '~src/ts/models/IDataSourceInfo';
import { IRemoteRefreshScheduleInfo } from '~src/ts/models/IRemoteRefreshScheduleInfo';
import { IWorkbookBase, IWorkbookInfo } from '~src/ts/models/IWorkbookInfo';
import { Set } from '~src/ts/models/Set';
import { CreateDatasourceExtractTaskActionService } from './CreateDatasourceExtractTaskAction';
import { CreateExtractTaskActionService, CreateExtractTasksMode, ExtractActionType, ExtractType, ICreateExtractTasksScope } from './CreateExtractTaskAction';
import { CreateWorkbookExtractTaskActionService } from './CreateWorkbookExtractTaskAction';


export namespace CreateMixedContentExtractTaskActionService {
  export function forContent(content: Set<IContentInfo>, context: IActionContext, allowedMode: CreateExtractTasksMode): Promise<IResult> {
    const workbooksAndDatasources: Set<ExtractType> = new Set(Set.PluckId);
    const ValidTypes = Set.fromKeyArray(['workbook', 'datasource']);
    content.each(item => {
      if (ValidTypes.containsKey(item.contentType)) {
        workbooksAndDatasources.add(item as ExtractType);
      }
    });

    const contentCount = workbooksAndDatasources.size();
    const firstItemName = L10n.elideIfLong(workbooksAndDatasources.first().name);
    const scheduleOnServerPrompt = Messages.schedulesLabelPromptContentItems({
      CONTENT_COUNT: contentCount,
      CONTENT_COUNT_LOC: L10n.localizedCount(contentCount),
      CONTENT_NAME: firstItemName
    });
    const scheduleOnClientPrompt = '';

    const refreshNowPrompt = Messages.extractTasksRefreshNowOnContentPrompt({
      CONTENT_COUNT: contentCount,
      CONTENT_COUNT_LOC: L10n.localizedCount(contentCount),
      CONTENT_NAME: L10n.elideIfLong(firstItemName)
    });

    return CreateExtractTaskActionService.execute(
      workbooksAndDatasources.clone(),
      context,
      scheduleOnServerPrompt,
      scheduleOnClientPrompt,
      (ids: string[], scheduleId: string, type: string) =>
        createExtractTasksForContent(workbooksAndDatasources, scheduleId, type),
      (dummyScope: ICreateExtractTasksScope, id: string, schedule: IRemoteRefreshScheduleInfo) => { return null; },
      () => Promise.resolve({}),
      refreshNowPrompt,
      (ids: string[], type: string) => refreshExtractsForContent(workbooksAndDatasources, type),
      (schedule: ISchedule) => contentErrorDetailFactory(schedule),
      allowedMode
    );
  }

  export function createExtractTasksForContent(content: Set<ExtractType>, scheduleId: string, type: string): Promise<IResult> {
    const workbooksPromise = Workbooks.createExtractTask(content.filter(et => 'workbook' === et.contentType).map(w => w.id), scheduleId, type);
    const datasourcesPromise = DataSources.createExtractTask(content.filter(et => 'datasource' === et.contentType).map(d => d.id), scheduleId, type);

    return Promise.all([workbooksPromise, datasourcesPromise]).then((resultsArray: IResult[]) => {
      return {
        errors: [...resultsArray[0].errors, ...resultsArray[1].errors]
      };
    });
  }

  export function refreshExtractsForContent(content: Set<IContentInfo>, type: string): Promise<IResult> {
    const workbooksPromise = Workbooks.refreshExtracts(content.filter(et => 'workbook' === et.contentType).map(w => w.id), type);
    const datasourcesPromise = DataSources.refreshExtracts(content.filter(et => 'datasource' === et.contentType).map(d => d.id), type);

    return Promise.all([workbooksPromise, datasourcesPromise]).then((resultsArray: IResult[]) => {
      return {
        errors: [...resultsArray[0].errors, ...resultsArray[1].errors]
      };
    });
  }

  export function contentErrorDetailFactory(schedule: ISchedule): (errorItem: IErrorItem<ExtractType>) => IErrorDetail {
    const workbookErrorDetailFactory = CreateWorkbookExtractTaskActionService.workbookErrorDetailFactory(schedule);
    const datasourceErrorDetailFactory = CreateDatasourceExtractTaskActionService.datasourceErrorDetailFactory(schedule);

    return (errorItem: IErrorItem<ExtractType>): IErrorDetail => {
      return 'workbook' === errorItem.item.contentType ?
        workbookErrorDetailFactory(errorItem as IErrorItem<IWorkbookBase>) :
        datasourceErrorDetailFactory(errorItem as IErrorItem<IDataSourceBase>);
    };
  }

  export function pollExtractActionStatus(contentItem: IWorkbookInfo | IDataSourceInfo, actionType: string, result: IResult) {
    ActionHelpers.displayStandardToastsForResult(
      Set.singletonForIdItem(contentItem),
      result,
      messagesForExtractActionStatus(actionType),
      (errorItem: IErrorItem<IWorkbookBase>) => contentErrorDetailFactory(null)(errorItem)
    );
  }

  export function messagesForExtractActionStatus(actionType: string): StandardActionToastMessages {
    if (actionType === ExtractActionType.createExtracts) {
      return {
        success: standardActionSuccessMsg(Messages.extractCreationJobSuccess),
        error: standardActionErrorMsg(Messages.extractCreationJobFailed),
      };
    } else if (actionType === ExtractActionType.encryptExtract) {
      return {
        success: standardActionSuccessMsg(Messages.extractEncryptionJobSuccess),
        error: standardActionErrorMsg(Messages.extractEncryptionJobFailed),
      };
    } else {
      return {
        success: standardActionSuccessMsg(Messages.extractDecryptionJobSuccess),
        error: standardActionErrorMsg(Messages.extractDecryptionJobFailed),
      };
    }
  }

}